
import { defineComponent } from "vue";
import { signOut } from "firebase/auth";
import { auth } from "@/firebase";
import { store } from "@/store";
import { isOperator } from "@shared/entities/System/OperatorAllowlist";

// Margen que damos antes de cerrar la sesión. No es estético: es el tiempo que
// tarda una persona en leer POR QUÉ se le va a cerrar. Cerrar en el `mounted()`
// deja exactamente la pantalla que el encargo prohíbe — la sesión desaparece y
// el aviso queda en pasado, sin que nadie lo haya visto venir.
const SEGUNDOS_DE_MARGEN = 10;

type EstadoSesion = "ninguna" | "exenta" | "por-cerrar" | "cerrada";

export default defineComponent({
  name: "updating-system",
  data() {
    return {
      estado: "ninguna" as EstadoSesion,
      segundos: SEGUNDOS_DE_MARGEN,
      // Guardados para poder cancelarlos si la persona sale de la pantalla
      // antes de que salte el cierre (por ejemplo pulsando el logo).
      intervalo: 0,
      cerrando: false,
    };
  },
  computed: {
    notice(): string {
      switch (this.estado) {
        case "por-cerrar":
          return "Mientras dura la actualización vamos a cerrar tu sesión. No tienes que hacer nada.";
        case "cerrada":
          return "Tu sesión se ha cerrado. Vuelve a entrar cuando terminemos.";
        case "exenta":
          return "Tu sesión sigue abierta.";
        default:
          // Quien llega aquí sin sesión no necesita que le hablemos de una.
          return "";
      }
    },
    countdown(): string {
      if (this.estado !== "por-cerrar") return "";
      if (this.segundos <= 1) return "Se cerrará en 1 segundo.";
      return `Se cerrará en ${this.segundos} segundos.`;
    },
  },
  mounted() {
    // `auth.currentUser` ya está resuelto aquí: el guard del router espera a
    // `waitForAuthReady()` (router.ts) antes de decidir a dónde te manda, así
    // que cuando esta pantalla se monta, Firebase Auth ya dijo si hay sesión o
    // no. No hace falta volver a escuchar `onAuthStateChanged`.
    const usuario = auth.currentUser;
    if (!usuario) {
      this.estado = "ninguna";
      return;
    }
    // Misma comprobación, con el mismo dato, que el `bypassesOffline` del
    // guard (router.ts): quien está exento del congelado no puede quedarse sin
    // sesión aquí. A un operador el guard NO lo trae a esta pantalla, pero sí
    // puede llegar por el botón «atrás» del navegador o escribiendo la ruta —
    // y cerrarle la sesión le vaciaría además el `localStorage`, que es donde
    // la consola de migración guarda qué pasos lleva marcados de la corrida.
    if (isOperator(usuario.displayName)) {
      this.estado = "exenta";
      return;
    }
    this.estado = "por-cerrar";
    this.intervalo = window.setInterval(() => {
      this.segundos -= 1;
      if (this.segundos <= 0) {
        this.pararReloj();
        this.cerrarSesion();
      }
    }, 1000);
  },
  beforeUnmount() {
    this.pararReloj();
  },
  methods: {
    pararReloj() {
      if (this.intervalo) {
        window.clearInterval(this.intervalo);
        this.intervalo = 0;
      }
    },
    async cerrarSesion() {
      if (this.cerrando) return;
      this.cerrando = true;
      try {
        // El camino de siempre, el mismo que usa el menú de perfil: además de
        // `signOut` desengancha todos los `onSnapshot` de la persona (perfil,
        // comisiones, pagos, suscripciones, puntos, retiros, clases, MMI,
        // vault…) y vacía los módulos del store. Si se cerrara la sesión a
        // mano, esos listeners seguirían vivos contra Firestore con un token
        // que ya no vale y llenarían la consola de errores de permisos.
        await store.dispatch("auth/logout");
      } catch (error) {
        // `auth/logout` hace un `Promise.all` de limpiezas ANTES del `signOut`:
        // si una de ellas fallara, la sesión se quedaría abierta, que es justo
        // lo que esta pantalla existe para evitar. Red de seguridad, no camino
        // alternativo.
        // eslint-disable-next-line no-console
        console.error("Fallo al cerrar la sesión en /offline:", error);
        try {
          await signOut(auth);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("Tampoco se pudo cerrar la sesión de Firebase:", e);
        }
      }
      // NO navegamos. Quedarse en `/offline` es la única salida estable: el
      // guard sólo se evalúa al navegar, así que sin navegación no hay forma de
      // volver a entrar aquí ni de repetir el cierre. Empujar a `/login`
      // rebotaría contra el propio guard (congelado + sin sesión ⇒ `/offline`)
      // y nos devolvería a esta misma pantalla dando un rodeo.
      this.estado = "cerrada";
    },
  },
});
